import { Fragment, useState } from 'react'

import { StatusBadge } from '@/components/material/StatusBadge'
import { Badge } from '@/components/ui/Badge'
import type { Candidate, FieldResult, SourceSummary } from '@/types/material'

const GATE_LABEL: Record<keyof Candidate['gates'], string> = {
  boundary: '边界匹配',
  normalized: '归一等价',
  cooccur: '参数名共现',
  anchored: '型号锚定',
}

function failedGates(candidate: Candidate) {
  return (Object.keys(GATE_LABEL) as (keyof Candidate['gates'])[]).filter((k) => !candidate.gates[k])
}

interface Props {
  fields: FieldResult[]
  sources: SourceSummary[]
}

/** 字段八态表：每行给出用户值 / 判定 / 采用值 / 证据强度，展开看候选与四道门 */
export function FieldTable({ fields, sources }: Props) {
  const [open, setOpen] = useState<string[]>([])
  const titleOf = (id: string) => (id === 'user' ? '用户原话' : (sources.find((s) => s.id === id)?.title ?? id))

  const toggle = (name: string) =>
    setOpen((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]))

  if (fields.length === 0) {
    return <p className="px-3 py-6 text-center text-xs text-muted">未识别到品类必填项</p>
  }

  return (
    <table className="w-full border-collapse text-[13px]">
      <thead>
        <tr className="border-b border-line text-left text-xs text-muted">
          <th className="px-3 py-2 font-medium">字段</th>
          <th className="px-2 py-2 font-medium">用户值</th>
          <th className="px-2 py-2 font-medium">判定</th>
          <th className="px-2 py-2 font-medium">采用值</th>
          <th className="px-2 py-2 font-medium">证据</th>
          <th className="px-2 py-2" />
        </tr>
      </thead>
      <tbody>
        {fields.map((field) => {
          const expanded = open.includes(field.name)
          return (
            <Fragment key={field.name}>
              <tr className="border-b border-line/70 align-top hover:bg-surface-2/60">
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium text-ink">{field.name}</span>
                    <Badge tone={field.tier === 'must' ? 'brand' : 'default'}>
                      {field.tier === 'must' ? '必填' : '重要'}
                    </Badge>
                  </div>
                  {field.when && <p className="mt-0.5 text-[11px] text-muted">条件必填：{field.when}</p>}
                </td>
                <td className="px-2 py-2 text-ink">{field.userValue ?? '—'}</td>
                <td className="px-2 py-2">
                  <StatusBadge status={field.status} />
                </td>
                <td className="px-2 py-2">
                  {field.bestValue ? (
                    <div>
                      <span className="font-medium text-ink">{field.bestValue}</span>
                      {field.normalizedValue && field.normalizedValue !== field.bestValue && (
                        <span className="ml-1 text-[11px] text-muted">归一 {field.normalizedValue}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td className="px-2 py-2 text-[12px] text-muted">
                  独立 {field.evidence.independentCount}
                  {field.evidence.topGrade && <span className="ml-1">· {field.evidence.topGrade}</span>}
                  {field.evidence.modelAnchored && <span className="ml-1">· 已锚定</span>}
                </td>
                <td className="px-2 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => toggle(field.name)}
                    className="rounded-control px-2 py-1 text-[12px] text-brand-600 transition-colors hover:bg-brand-50"
                  >
                    {expanded ? '收起' : `候选 ${field.candidates.length}`}
                  </button>
                </td>
              </tr>
              {expanded && (
                <tr className="border-b border-line/70 bg-surface-2/40">
                  <td colSpan={6} className="px-3 py-2">
                    {field.action && <p className="mb-2 text-[12px] leading-relaxed text-ink">{field.action}</p>}
                    {field.candidates.length === 0 ? (
                      <p className="text-[12px] text-muted">无候选值：来源里没找到该字段的料</p>
                    ) : (
                      <ul className="flex flex-col gap-1">
                        {field.candidates.map((candidate, i) => {
                          const failed = failedGates(candidate)
                          return (
                            <li key={`${candidate.sourceId}-${candidate.value}-${i}`} className="text-[12px] leading-relaxed">
                              <span className={candidate.passes ? 'font-medium text-ink' : 'text-muted line-through'}>
                                {candidate.value}
                              </span>
                              <span className="ml-1.5 text-muted">
                                《{titleOf(candidate.sourceId)}》 {candidate.grade}
                                {candidate.via === 'model_decode' ? ' · 型号解码' : ''}
                                {candidate.via === 'llm_extract' ? ' · AI 摘录' : ''}
                                {candidate.weak ? ' · 弱特征' : ''}
                                {!candidate.passes ? ' · 未过门' : ''}
                              </span>
                              {failed.length > 0 && (
                                <span className="ml-1.5 text-[11px] text-warn">缺 {failed.map((g) => GATE_LABEL[g]).join('/')}</span>
                              )}
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </td>
                </tr>
              )}
            </Fragment>
          )
        })}
      </tbody>
    </table>
  )
}
